/**
 * PredimToolbar — export buttons + save button for a predim sub-form.
 *
 * Drop-in replacement for the existing `<ExportButtons>` widget used by
 * each `PredimXxx` component. Wraps:
 *   - The PDF / XLSX export pair (cost 1 fierro each via require_tokens).
 *   - A "Guardar" button that opens SaveDesignDialog scoped to the
 *     predim module + the `family:variant` subtype.
 *
 * Save semantics:
 *   - `existing` non-null → PATCH (update) — backend doesn't re-charge.
 *   - `existing` null     → POST  (create) — backend charges 1 fierro.
 *
 * Both behaviors are transparent to the caller; the dialog/backend
 * handle the difference internally. The toolbar renders consistently
 * either way.
 */

import { useState } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import type { DesignFull } from '@/features/designs/types';
import { ExportButtons } from './shared';

export interface PredimToolbarProps {
  family: 'columns' | 'beams' | 'slabs' | 'walls' | 'footings';
  variant: string;
  /** The current input state of the predim sub-form — captured as-is. */
  inputs: Record<string, unknown>;
  /** When the page is loaded from /app/predim/:id, the existing design row. */
  existing?: DesignFull | null;
}

export function PredimToolbar({ family, variant, inputs, existing }: PredimToolbarProps) {
  const [saveOpen, setSaveOpen] = useState(false);

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <ExportButtons family={family} inputs={inputs} />
      <Button
        variant="primary"
        size="sm"
        onClick={() => setSaveOpen(true)}
        title={existing ? 'Actualizar el predim guardado' : 'Guardar este predim (cuesta 1 fierro)'}
      >
        {false ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} strokeWidth={2} />}
        {existing ? 'Actualizar' : 'Guardar'}
      </Button>

      <SaveDesignDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        module="predim"
        // `subtype` encodes "family:variant" so the index list can show
        // the right badge and the loader can re-select the right form.
        subtype={`${family}:${variant}`}
        payload={{ family, variant, inputs }}
        existing={existing ?? null}
      />
    </div>
  );
}
